import type * as React from 'react';
import { useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import type { ContactTemplate, ContactTemplateInput } from '../api/contact-templates';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contacts: ContactTemplate[];
  onSave: (input: ContactTemplateInput, id?: string) => Promise<ContactTemplate>;
  onRemove: (id: string) => Promise<void>;
  /** Сохранённый шаблон сразу выбирается в форме отчёта. */
  onSaved?: (contact: ContactTemplate) => void;
};

const EMPTY: ContactTemplateInput = { name: '', role: '', email: '', phone: '', telegram: '' };

const FIELDS: Array<{ key: keyof ContactTemplateInput; label: string; placeholder: string }> = [
  { key: 'name', label: 'Имя', placeholder: 'Адина Макашова' },
  { key: 'role', label: 'Должность', placeholder: 'Account manager' },
  { key: 'email', label: 'Почта', placeholder: 'adina.makashova@galamat.media' },
  { key: 'phone', label: 'Телефон', placeholder: '8 (702) 000 10 03' },
  { key: 'telegram', label: 'Telegram', placeholder: '@adinamk' },
];

/** Шаблоны контактов для слайда «Мы всегда на связи!» — общие для команды. */
export function ContactTemplatesDialog({
  open,
  onOpenChange,
  contacts,
  onSave,
  onRemove,
  onSaved,
}: Props): React.ReactElement {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ContactTemplateInput>(EMPTY);
  const [isSaving, setIsSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  function startEdit(contact: ContactTemplate): void {
    setEditingId(contact.id);
    setForm({
      name: contact.name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
      telegram: contact.telegram,
    });
  }

  function resetForm(): void {
    setEditingId(null);
    setForm(EMPTY);
  }

  async function handleSave(): Promise<void> {
    if (!form.name.trim()) return;
    setIsSaving(true);
    try {
      const saved = await onSave(form, editingId ?? undefined);
      toast.success(editingId ? 'Контакт обновлён' : 'Контакт добавлен');
      onSaved?.(saved);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось сохранить контакт');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemove(contact: ContactTemplate): Promise<void> {
    if (!window.confirm(`Удалить контакт «${contact.name}»? Он пропадёт у всей команды.`)) return;
    setRemovingId(contact.id);
    try {
      await onRemove(contact.id);
      if (editingId === contact.id) resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось удалить контакт');
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      {/* Колонка grid ограничена, иначе длинная строка контакта распирает окно и truncate не срабатывает. */}
      <DialogContent className="max-h-[90vh] grid-cols-[minmax(0,1fr)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Шаблоны контактов</DialogTitle>
          <DialogDescription>
            Контакты для последнего слайда презентации. Список общий для всей команды.
          </DialogDescription>
        </DialogHeader>

        {contacts.length > 0 && (
          <ul className="divide-y rounded-md border">
            {contacts.map((c) => (
              <li key={c.id} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{c.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[c.role, c.email, c.phone, c.telegram].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Изменить ${c.name}`}
                  onClick={() => startEdit(c)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Удалить ${c.name}`}
                  disabled={removingId === c.id}
                  onClick={() => void handleRemove(c)}
                >
                  {removingId === c.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <p className="text-sm font-medium">{editingId ? 'Изменение контакта' : 'Новый контакт'}</p>
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={`contact-${f.key}`}>{f.label}</Label>
              <Input
                id={`contact-${f.key}`}
                value={form[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                disabled={isSaving}
                required={f.key === 'name'}
              />
            </div>
          ))}
          <div className="flex gap-2">
            <Button type="submit" disabled={isSaving || !form.name.trim()} className="gap-2">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editingId ? 'Сохранить' : 'Добавить'}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm} disabled={isSaving}>
                Отмена
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
